import * as CryptoJS from "crypto-js";

/** 加密参数配置 */
interface EncryptionOptions {
  data: Record<string, any>;
  type?: "Base64" | string;
  param: string[];
  key: string;
}

/**
 * AES 加密处理
 * 默认使用 CBC 模式 + ZeroPadding + Latin1 解析密钥
 */
export const encryption = (options: EncryptionOptions): Record<string, any> => {
  let { data, type, param, key } = options;
  const result = JSON.parse(JSON.stringify(data));

  if (type === "Base64") {
    param.forEach((ele: string) => {
      result[ele] = btoa(result[ele]);
    });
  } else {
    param.forEach((ele: string) => {
      const plainText = result[ele];
      const parsedKey = CryptoJS.enc.Latin1.parse(key);
      const iv = parsedKey;
      const encrypted = CryptoJS.AES.encrypt(plainText, parsedKey, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.ZeroPadding,
      });
      result[ele] = encrypted.toString();
    });
  }
  return result;
};

/** MD5 签名参数配置 */
interface SignOptions {
  [key: string]: any;
}

/**
 * MD5 加密生成 sign
 * 1. 过滤空值和 undefined
 * 2. 参数按字母升序排列
 * 3. 拼接 &secret= 后 MD5 取值
 */
export const generateSign = (
  params: SignOptions,
  secretKey: string
): string => {
  const validParams: Record<string, string> = {};
  Object.keys(params).forEach((key) => {
    if (key && key.trim() !== "") {
      validParams[key] = params[key] != null ? String(params[key]) : "";
    }
  });

  const sortedKeys = Object.keys(validParams).sort((a, b) =>
    a.localeCompare(b, "en-US", { numeric: false })
  );

  const paramsStr = sortedKeys
    .map((key) => `${key}=${validParams[key]}`)
    .join("&");
  const stringToSign = `${paramsStr}&secret=${secretKey}`;

  return CryptoJS.MD5(stringToSign).toString();
};

/** 文件下载 MIME 类型映射 */
const DOWNLOAD_TYPE_MAP = {
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  pdf: "application/pdf",
  mp4: "video/mp4",
  webm: "video/webm",
  mov: "video/quicktime",
  avi: "video/x-msvideo",
  wmv: "video/x-ms-wmv",
  mkv: "video/x-matroska",
  flv: "video/x-flv",
  m4v: "video/x-m4v",
  mpeg: "video/mpeg",
  "3gp": "video/3gpp",
  mp3: "audio/mpeg",
  wav: "audio/wav",
  ogg: "audio/ogg",
  m4a: "audio/x-m4a",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  webp: "image/webp",
  bmp: "image/bmp",
  svg: "image/svg+xml",
  ico: "image/x-icon",
  tiff: "image/tiff",
} as const;

/** 文件扩展名类型 */
type SuffixType = keyof typeof DOWNLOAD_TYPE_MAP;

/**
 * 通过a标签下载文件
 * @param obj - Blob对象或响应数据
 * @param name - 文件名（不含扩展名）
 * @param suffix - 文件扩展名（xls/xlsx/doc/docx/pdf等）
 */
export function _downloadFile(
  obj: Blob | string | ArrayBuffer,
  name: string,
  suffix: SuffixType
) {
  if (!DOWNLOAD_TYPE_MAP[suffix]) {
    throw new Error("请传入文件下载的格式后缀，eg：xls，xlsx，doc，docx，pdf");
  }

  let blob: Blob;

  // 如果传入的已经是 Blob，优先保留其原始 type，不强制替换
  // 避免因 MIME type 不匹配导致文件打开乱码
  if (obj instanceof Blob) {
    blob = obj; // 直接使用原始 Blob，保留服务端返回的正确 type
  } else {
    // 兼容其他格式（如 base64 字符串等）
    blob = new Blob([obj], { type: DOWNLOAD_TYPE_MAP[suffix] });
  }

  const fileName = `${name}.${suffix}`;
  const link = document.createElement("a");
  document.body.appendChild(link);
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", fileName);
  link.click();
  document.body.removeChild(link);
  // 延迟撤销 blob URL，确保下载已开始
  setTimeout(() => URL.revokeObjectURL(link.href), 1000);
}
